import { useEffect, useRef, useState } from 'react';
import { OTP_RESEND_COOLDOWN_MS } from '@cowoo/shared';

/** § 5.2 — the confirmation pill stays up for four seconds. */
const SENT_PILL_MS = 4_000;
/** OtterAuth allows five codes per address per ten minutes (06 § 2). */
const RATE_LIMIT_MS = 10 * 60_000;

export interface ResendCooldown {
  /** Whole seconds left before the link opens again. */
  seconds: number;
  ready: boolean;
  /** True while the "Neuer Code ist unterwegs" pill is showing. */
  justSent: boolean;
  /** A new code went out: restart the cooldown and raise the pill. */
  restart(): void;
  /** OtterAuth refused on its own limit; shut the link for the full ten minutes. */
  block(): void;
}

/**
 * The resend row of 06 § 5.2 and 09 § 4.1.
 *
 * The first cooldown starts when the screen opens, because a code was sent on
 * the way here — otherwise the link would be open the moment the user arrives
 * and the second code would go out before the first one landed.
 */
export function useResendCooldown(): ResendCooldown {
  const [until, setUntil] = useState(() => Date.now() + OTP_RESEND_COOLDOWN_MS);
  const [sentAt, setSentAt] = useState<number | null>(null);
  const [, tick] = useState(0);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  // One interval for the whole row: it drives both the countdown and the pill,
  // and stops once neither has anything left to count.
  useEffect(() => {
    timer.current = setInterval(() => tick((n) => n + 1), 500);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, []);

  const now = Date.now();
  const remaining = Math.max(0, until - now);

  return {
    seconds: Math.ceil(remaining / 1000),
    ready: remaining === 0,
    justSent: sentAt !== null && now - sentAt < SENT_PILL_MS,
    restart: () => {
      setSentAt(Date.now());
      setUntil(Date.now() + OTP_RESEND_COOLDOWN_MS);
    },
    block: () => {
      setSentAt(null);
      setUntil(Date.now() + RATE_LIMIT_MS);
    },
  };
}
